import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

/**
 * A modal dialog (UX Stage B, B4): aria-modal with a title, focus moved in and kept inside (Tab wraps), Esc and the
 * backdrop close it (unless it is busy), focus goes back to what opened it, and the page behind doesn't scroll.
 *
 *   <Modal open={open} onClose={close} title="Edit balances" footer={<><Button onClick={close}>Cancel</Button>
 *          <Button variant="primary" onClick={save}>Save</Button></>}>…</Modal>
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  /** The buttons, right-aligned under the body. */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Where focus starts (default: the first field or button in the body, then the footer). */
  initialFocus?: React.RefObject<HTMLElement>;
  /** False while something runs: Esc, the backdrop and the close button do nothing. */
  dismissible?: boolean;
  /** For tests and screenshots. */
  testId?: string;
}

const WIDTH = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-6xl' };
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  open, onClose, title, description, children, footer, size = 'md', initialFocus, dismissible = true, testId,
}) => {
  const panel = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const descId = React.useId();
  const close = React.useRef(onClose);
  close.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    const back = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const p = panel.current;
    const body = p?.querySelector<HTMLElement>('[data-modal-body]');
    const target = initialFocus?.current
      ?? body?.querySelector<HTMLElement>(FOCUSABLE)
      ?? p?.querySelector<HTMLElement>('[data-modal-footer] ' + 'button:not([disabled])')
      ?? p;
    target?.focus();
    return () => { document.body.style.overflow = overflow; back?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (dismissible) close.current();
      return;
    }
    if (e.key !== 'Tab' || !panel.current) return;
    const f = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (f.length === 0) { e.preventDefault(); return; }
    const [first, last] = [f[0], f[f.length - 1]];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end justify-center p-mp-4 sm:items-center" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-mp-text opacity-40" aria-hidden data-testid="modal-backdrop"
           onClick={() => { if (dismissible) close.current(); }} />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descId : undefined}
           tabIndex={-1} data-testid={testId}
           className={`relative flex max-h-[90vh] w-full ${WIDTH[size]} flex-col rounded-mp-md border border-mp-border bg-mp-surface font-mp-sans text-mp-base text-mp-text shadow-xl focus:outline-none`}>
        <div className="flex items-start justify-between gap-mp-3 border-b border-mp-border px-mp-5 py-mp-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-mp-text">{title}</h2>
            {description && <p id={descId} className="mt-mp-1 text-mp-text-muted">{description}</p>}
          </div>
          <Button variant="ghost" size="sm" aria-label="Close" onClick={() => close.current()} disabled={!dismissible}
                  icon={<X className="h-4 w-4" />} className="-mr-mp-2 text-mp-text-muted" />
        </div>
        {children !== undefined && children !== null && (
          <div data-modal-body className="overflow-y-auto px-mp-5 py-mp-4">{children}</div>
        )}
        {footer && (
          <div data-modal-footer className="flex flex-wrap justify-end gap-mp-2 border-t border-mp-border px-mp-5 py-mp-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  /** What will happen, in plain words (what is lost, whether it can be undone). */
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Deletes, reverses or writes off: the confirm button is the destructive one and focus starts on Cancel. */
  destructive?: boolean;
  /** While the request runs: the confirm button spins and the dialog can't be dismissed. */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** A yes/no question before an action. The safe choice has focus to begin with when the action is destructive. */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = false, loading = false, onConfirm, onCancel,
}) => {
  const cancel = React.useRef<HTMLButtonElement>(null);
  const ok = React.useRef<HTMLButtonElement>(null);
  return (
    <Modal open={open} onClose={onCancel} title={title} size="sm" dismissible={!loading} testId="confirm-dialog"
           initialFocus={destructive ? cancel : ok}
           footer={<>
             <Button ref={cancel} variant="secondary" onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
             <Button ref={ok} variant={destructive ? 'destructive' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
           </>}>
      {message && <div className="text-mp-text">{message}</div>}
    </Modal>
  );
};

export interface ConfirmOptions {
  title: React.ReactNode;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type Ask = (o: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = React.createContext<Ask | null>(null);

/** Mount once near the root; then useConfirm() anywhere below asks with a ConfirmDialog. */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = React.useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const ask = React.useCallback<Ask>(o => new Promise<boolean>(resolve => setPending({ ...o, resolve })), []);
  const answer = (v: boolean) => { pending?.resolve(v); setPending(null); };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <ConfirmDialog open={!!pending} title={pending?.title} message={pending?.message} confirmLabel={pending?.confirmLabel}
                     cancelLabel={pending?.cancelLabel} destructive={pending?.destructive}
                     onConfirm={() => answer(true)} onCancel={() => answer(false)} />
    </ConfirmContext.Provider>
  );
};

/** `if (!(await confirm({ title: 'Delete this rule?', destructive: true }))) return;` Replaces window.confirm. Outside
 *  a ConfirmProvider (a component test) it falls back to window.confirm. */
export function useConfirm(): Ask {
  const ask = React.useContext(ConfirmContext);
  return React.useCallback<Ask>(
    o => (ask ? ask(o) : Promise.resolve(window.confirm([o.title, o.message].filter(x => typeof x === 'string').join('\n\n')))),
    [ask],
  );
}
